// src/pages/kioskSearchPage.js
// Kiosk / Totem self-lookup page — no menu, no cart, no stock toggles, no row selection
// Includes digital signage: auto-play campaign after 2 min inactivity + play button
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Barcode from '../components/search/BarcodeScan.gif';
import { BiBarcodeReader } from 'react-icons/bi';
import { BsPlayCircle } from 'react-icons/bs';
import NewSearchTable from "../components/search/basicsearchtable";
import CampaignPlayer from '../components/signage/CampaignPlayer';
import { getCurrentBookedCampaign } from '../services/signageService';

import { useAuth } from '../context/AuthContext';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import './searchpage.css';
import './signagepage.css';
import { NODE_BACKEND_SCH_URL, NODE_BACKEND_API_URL } from "../config";

const SEARCH_API_URL = NODE_BACKEND_SCH_URL;
const GET_CATEGORY_NAMES_URL = `${NODE_BACKEND_API_URL}/getcategorynames`;

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
Alert.displayName = 'KioskSnackbarAlert';

function KioskSearchPage() {
    const { interceptorsReady, user } = useAuth();
    const navigate = useNavigate();
    // GIULIANO: si el feature de credit card markup esta activo (no hay Modo Totem), redirigir a /search
    useEffect(() => {
        if (user && user.creditCardMarkupEnabled) {
            navigate('/search');
        }
    }, [user, navigate]);
    const searchInputRef = useRef(null);
    const barcodeInputRef = useRef(null);
    const tableRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResultsTable, setShowResultsTable] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All categories');
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Barcode modal states
    const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [isLoadingBarcodeSearch, setIsLoadingBarcodeSearch] = useState(false);

    // ── Digital Signage States ──
    const [isPlayingCampaign, setIsPlayingCampaign] = useState(false);
    const [bookedCampaign, setBookedCampaign] = useState(null);
    const [countdown, setCountdown] = useState(null);
    const inactivityTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const INACTIVITY_TIMEOUT = 2 * 60 * 1000; // 2 minutes
    const COUNTDOWN_START = 30; // seconds before autoplay to show countdown

    // Load the currently booked campaign — wait for auth interceptors to be ready
    // before making the request so the JWT is attached and we don't get a 401.
    useEffect(() => {
        if (!interceptorsReady) return;
        const loadBookedCampaign = async () => {
            const campaign = await getCurrentBookedCampaign();
            setBookedCampaign(prev => {
                const prevId = prev?.id ?? null;
                const newId = campaign?.id ?? null;
                return prevId === newId ? prev : campaign;
            });
        };
        loadBookedCampaign();
        // Check periodically (every 60s) in case schedule changes
        const interval = setInterval(loadBookedCampaign, 60000);
        return () => clearInterval(interval);
    }, [interceptorsReady]);

    // Reset inactivity timer on any user interaction
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        setCountdown(null);
        if (!bookedCampaign || isPlayingCampaign) return;

        // Start countdown display after (INACTIVITY_TIMEOUT - 30s)
        countdownTimerRef.current = setTimeout(() => {
            setCountdown(COUNTDOWN_START);
            countdownIntervalRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownIntervalRef.current);
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);
        }, INACTIVITY_TIMEOUT - COUNTDOWN_START * 1000);

        inactivityTimerRef.current = setTimeout(() => {
            setIsPlayingCampaign(true);
            setCountdown(null);
        }, INACTIVITY_TIMEOUT);
    }, [bookedCampaign, isPlayingCampaign, INACTIVITY_TIMEOUT]);

    useEffect(() => {
        const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
        events.forEach(evt => document.addEventListener(evt, resetInactivityTimer));
        resetInactivityTimer(); // Start timer on mount
        return () => {
            events.forEach(evt => document.removeEventListener(evt, resetInactivityTimer));
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [resetInactivityTimer]);

    const handleExitCampaign = useCallback(() => {
        setIsPlayingCampaign(false);
        resetInactivityTimer();
    }, [resetInactivityTimer]);

    const handlePlayCampaign = useCallback(() => {
        if (bookedCampaign) setIsPlayingCampaign(true);
    }, [bookedCampaign]);

    // ── Auto-focus search input & Arrow key pagination ──
    useEffect(() => {
        const focusSearch = () => {
            if (isPlayingCampaign || isBarcodeModalOpen) return;
            const input = searchInputRef.current?.querySelector('input') || searchInputRef.current;
            if (input && document.activeElement !== input) {
                input.focus();
            }
        };
        // Focus on mount and whenever player/modal closes
        focusSearch();
        const interval = setInterval(focusSearch, 500);
        return () => clearInterval(interval);
    }, [isPlayingCampaign, isBarcodeModalOpen]);

    useEffect(() => {
        const handleArrowKeys = (e) => {
            if (isPlayingCampaign) return;
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                tableRef.current?.nextPage();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                tableRef.current?.prevPage();
            }
        };
        document.addEventListener('keydown', handleArrowKeys);
        return () => document.removeEventListener('keydown', handleArrowKeys);
    }, [isPlayingCampaign]);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);

    const fetchSearchResults = useCallback(async (currentSearchTerm, currentCategoryName) => {
        if (!currentSearchTerm.trim()) {
            setSearchResults([]);
            setShowResultsTable(false);
            return;
        }

        setIsLoadingSearch(true);
        setShowResultsTable(false);

        try {
            let apiUrl = `${SEARCH_API_URL}?search=${encodeURIComponent(currentSearchTerm)}&includeStockedOnly=false&includeFavoritesOnly=false`;

            if (currentCategoryName && currentCategoryName !== 'All categories') {
                apiUrl += `&categoryName=${encodeURIComponent(currentCategoryName)}`;
            }

            const response = await axios.get(apiUrl);
            setSearchResults(response.data);
            setShowResultsTable(true);
        } catch (error) {
            console.error('Error fetching search data:', error);
            setSearchResults([]);
            setShowResultsTable(true);
            showSnackbar("Error al realizar la búsqueda. Intente de nuevo.", "error");
        } finally {
            setIsLoadingSearch(false);
            setTimeout(() => {
                if (searchInputRef.current) {
                    const inputElement = searchInputRef.current.querySelector('input') || searchInputRef.current;
                    inputElement.focus();
                    inputElement.select();
                }
            }, 100);
        }
    }, [showSnackbar]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        fetchSearchResults(searchTerm, selectedCategory);
    }, [searchTerm, selectedCategory, fetchSearchResults]);

    const handleInputChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);

    const handleCategoryChange = useCallback((event) => {
        const newCategory = event.target.value;
        setSelectedCategory(newCategory);
        fetchSearchResults(searchTerm, newCategory);
    }, [searchTerm, fetchSearchResults]);

    // Barcode modal handlers
    const handleOpenBarcodeModal = useCallback(() => {
        setIsBarcodeModalOpen(true);
        setBarcodeInput('');
        setTimeout(() => {
            if (barcodeInputRef.current) {
                barcodeInputRef.current.focus();
            }
        }, 300);
    }, []);

    const handleCloseBarcodeModal = useCallback(() => {
        setIsBarcodeModalOpen(false);
        setBarcodeInput('');
        setIsLoadingBarcodeSearch(false);
    }, []);

    const handleBarcodeInputChange = useCallback((event) => {
        setBarcodeInput(event.target.value);
    }, []);

    const handleBarcodeSearch = useCallback(async () => {
        if (!barcodeInput.trim()) {
            showSnackbar("Ingrese al menos un código de barras.", "warning");
            return;
        }

        setIsLoadingBarcodeSearch(true);

        try {
            const barcodes = barcodeInput
                .split('\n')
                .map(code => code.trim())
                .filter(code => code.length > 0);

            if (barcodes.length === 0) {
                showSnackbar("No se encontraron códigos de barras válidos.", "warning");
                setIsLoadingBarcodeSearch(false);
                return;
            }

            const response = await axios.post(`${NODE_BACKEND_API_URL}/getbatchsearch`, {
                eans: barcodes
            });

            const transformedData = response.data.map(item => ({
                ...item,
                BrandName: item.Brand
            }));

            setSearchResults(transformedData);
            setShowResultsTable(true);

            showSnackbar(`Búsqueda por lotes completada. ${response.data.length} productos encontrados.`, "success");
            handleCloseBarcodeModal();

            setTimeout(() => {
                if (searchInputRef.current) {
                    const inputElement = searchInputRef.current.querySelector('input') || searchInputRef.current;
                    inputElement.focus();
                    inputElement.select();
                }
            }, 200);

        } catch (error) {
            console.error('Error en búsqueda por lotes:', error);
            showSnackbar("Error al realizar la búsqueda por lotes. Intente de nuevo.", "error");
        } finally {
            setIsLoadingBarcodeSearch(false);
        }
    }, [barcodeInput, showSnackbar, handleCloseBarcodeModal]);

    useEffect(() => {
        if (!interceptorsReady) return;
        const timer = setTimeout(() => {
            const fetchCategories = async () => {
                setIsLoadingCategories(true);
                try {
                    const response = await axios.get(GET_CATEGORY_NAMES_URL);
                    setCategories(response.data);
                } catch (error) {
                    console.error('Error fetching categories:', error);
                    showSnackbar("Error al cargar las categorías disponibles.", "error");
                } finally {
                    setIsLoadingCategories(false);
                }
            };
            fetchCategories();
        }, 100);
        return () => clearTimeout(timer);
    }, [showSnackbar, interceptorsReady]);

    const sortedSearchResultsForTable = useMemo(() => {
        return [...searchResults].sort((a, b) => {
            return String(a.ProductDescription).localeCompare(String(b.ProductDescription));
        });
    }, [searchResults]);

    return (
        <div className="search-main-div">
            <div className="parent-div" style={{ paddingTop: '15vh', paddingLeft: '8%' }}>
                <div className="top-grid">
                    <Box className="search-controls-container" sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        paddingX: '1rem'
                    }}>
                        <form onSubmit={handleSubmit}>
                            <Box sx={{ display: 'flex', gap: 1, width: '40vw', paddingY: '1rem' }}>
                                <TextField
                                    ref={searchInputRef}
                                    label="¿Qué estás buscando?"
                                    variant="outlined"
                                    fullWidth
                                    onChange={handleInputChange}
                                    value={searchTerm}
                                    disabled={isLoadingSearch}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={isLoadingSearch || !searchTerm.trim()}
                                >
                                    Buscar
                                </Button>
                                <IconButton
                                    color="primary"
                                    onClick={handleOpenBarcodeModal}
                                    disabled={isLoadingSearch}
                                    sx={{
                                        border: '1px solid #1976d2',
                                        borderRadius: '4px',
                                        padding: '8px'
                                    }}
                                >
                                    <BiBarcodeReader size={20} />
                                </IconButton>
                            </Box>
                        </form>
                    </Box>

                    <div className="bottom-div">
                        <div className="tablediv">
                            {isLoadingSearch && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {!isLoadingSearch && showResultsTable && searchResults.length > 0 ? (
                                <NewSearchTable
                                    ref={tableRef}
                                    data={sortedSearchResultsForTable}
                                    hasStockData={false}
                                    hideSelection={true}
                                    hideFavorites={true}
                                    totemMode={true}
                                    currentSearchTerm={searchTerm}
                                />
                            ) : (
                                !isLoadingSearch && showResultsTable && searchResults.length === 0 ? (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        No se encontraron resultados para "{searchTerm}".
                                    </Alert>
                                ) : (
                                    !isLoadingSearch && (
                                        <div className="barcode-div">
                                            <img id='barcode' src={Barcode} alt="Barcode" />
                                        </div>
                                    )
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Barcode Search Modal */}
            <Modal
                open={isBarcodeModalOpen}
                onClose={handleCloseBarcodeModal}
                aria-labelledby="kiosk-barcode-modal-title"
                aria-describedby="kiosk-barcode-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '500px',
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px'
                }}>
                    <Typography id="kiosk-barcode-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Búsqueda por Códigos de Barras
                    </Typography>
                    <Typography id="kiosk-barcode-modal-description" variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Ingrese los códigos de barras:
                    </Typography>

                    <TextField
                        ref={barcodeInputRef}
                        autoFocus
                        fullWidth
                        multiline
                        rows={8}
                        variant="outlined"
                        value={barcodeInput}
                        onChange={handleBarcodeInputChange}
                        placeholder="Código de barras 1&#10;Código de barras 2&#10;Código de barras 3&#10;..."
                        disabled={isLoadingBarcodeSearch}
                        sx={{ mb: 3 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseBarcodeModal}
                            disabled={isLoadingBarcodeSearch}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleBarcodeSearch}
                            disabled={isLoadingBarcodeSearch || !barcodeInput.trim()}
                            startIcon={isLoadingBarcodeSearch && <CircularProgress size={20} />}
                        >
                            {isLoadingBarcodeSearch ? 'Buscando...' : 'Buscar'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* ── Digital Signage: Play Button ── */}
            {bookedCampaign && !isPlayingCampaign && (
                <button
                    className="totem-signage-btn"
                    onClick={handlePlayCampaign}
                    title="Reproducir cartelería digital"
                >
                    <BsPlayCircle size={28} />
                    {countdown !== null && (
                        <span className="totem-signage-countdown">{countdown}</span>
                    )}
                </button>
            )}

            {/* ── Digital Signage: Fullscreen Campaign Player ── */}
            {isPlayingCampaign && bookedCampaign && (
                <CampaignPlayer
                    campaign={bookedCampaign}
                    onExit={handleExitCampaign}
                    loop={true}
                />
            )}
        </div>
    );
}

export default KioskSearchPage;
